import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import * as Sentry from '@sentry/react';
import { getApiBaseUrl } from '@core/tenant';
import { safeLocalStorage } from '@shared/utils/safeStorage';
import { captureAuthError } from '@core/sentry/authErrors';
import type { ApiErrorCode, ApiErrorResponse, RequestConfig } from './types';
import { buildAuthRequestBreadcrumbData, buildAuth401BreadcrumbData } from './authBreadcrumbs';

/**
 * Custom API Error class with additional context
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code: ApiErrorCode = 'UNKNOWN_ERROR',
    public errors?: Record<string, string[]>,
    public serverCode?: string,
    public endpoint?: string,
    public method?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Check if error is a network/connectivity issue
   */
  isNetworkError(): boolean {
    return this.code === 'NETWORK_ERROR' || this.code === 'TIMEOUT_ERROR';
  }

  /**
   * Check if error is an authentication issue
   */
  isAuthError(): boolean {
    return this.code === 'UNAUTHORIZED' || this.code === 'FORBIDDEN';
  }

  /**
   * Check if error is a validation issue
   */
  isValidationError(): boolean {
    return this.code === 'VALIDATION_ERROR';
  }
}

/**
 * Token storage key
 */
const AUTH_TOKEN_KEY = 'dineflow_auth_token';
// Storage key values intentionally preserved as `dineflow_auth_flow_id*` so
// sessions in flight at deploy time don't get their correlation id orphaned.
// Only the variable / field names flip to `flowId` to dodge Sentry's default
// @password:filter which redacts any key matching /auth|token|password/.
const FLOW_ID_KEY = 'dineflow_auth_flow_id';
const FLOW_ID_CREATED_AT_KEY = 'dineflow_auth_flow_id_created_at';
const FLOW_ID_TTL_MS = 15 * 60 * 1000;

/**
 * In-memory dedup window for `auth.unexpected_401` captures.
 *
 * A single logical auth failure can produce multiple 401s in quick succession
 * (background polling, prefetch, React Query refetch). Without dedup, Sentry
 * receives the same event repeatedly and every duplicate also fires
 * `auth:expired`, compounding the logout cascade.
 *
 * Key is `${flowId ?? 'none'}:${normalizedEndpoint}`. Entries expire 60s
 * after insertion. Per-tab memory — intentionally not persisted (a reload
 * means the user has already dealt with the situation).
 */
const AUTH_401_DEDUP_WINDOW_MS = 60_000;
const auth401DedupCache = new Map<string, number>();

export function shouldCapture401(
  flowId: string | null,
  endpoint: string | undefined,
): boolean {
  const key = `${flowId ?? 'none'}:${endpoint ?? 'unknown'}`;
  const now = Date.now();
  const lastSeen = auth401DedupCache.get(key);
  if (lastSeen !== undefined && now - lastSeen < AUTH_401_DEDUP_WINDOW_MS) {
    return false;
  }
  auth401DedupCache.set(key, now);
  // Bound growth: drop entries older than the window.
  if (auth401DedupCache.size > 100) {
    for (const [k, ts] of auth401DedupCache) {
      if (now - ts >= AUTH_401_DEDUP_WINDOW_MS) auth401DedupCache.delete(k);
    }
  }
  return true;
}

/** Test-only: reset the dedup cache between tests. */
export function _resetAuth401DedupCacheForTest(): void {
  auth401DedupCache.clear();
}

/**
 * Get stored auth token
 */
export function getAuthToken(): string | null {
  return safeLocalStorage.getItem(AUTH_TOKEN_KEY);
}

/**
 * Set auth token
 */
export function setAuthToken(token: string): void {
  safeLocalStorage.setItem(AUTH_TOKEN_KEY, token);
}

/**
 * Write the auth token to storage and verify it stuck.
 *
 * Accepts a possibly-missing token because the upstream OAuth response is
 * sometimes `{ token: undefined }` in in-app WebViews where the popup flow
 * fails partially. Before this guard, the `String(undefined)` coercion wrote
 * the literal `"undefined"` to storage and the next request sent
 * `Authorization: Bearer undefined`, which the backend rejected as an invalid
 * JWT. We now treat that as an up-front failure mode with its own Sentry
 * signal so the root cause stays visible.
 *
 * `safeLocalStorage.setItem` silently swallows write errors (quota exceeded,
 * storage disabled, Safari ITP). Those silent failures leave the user in a
 * "phantom auth" state: React state says authenticated, but subsequent
 * requests go out without the JWT and the backend rejects them.
 *
 * This wrapper writes then reads back. On mismatch we:
 *   1. Clear whatever is in storage so a stale prior-session token can't keep
 *      authenticating the user as someone else.
 *   2. Capture `auth.token_write_readback_mismatch` with lengths so Sentry can
 *      distinguish quota-driven drops from storage-disabled contexts.
 *
 * Returns true if the token is in storage, false otherwise. Callers should
 * abort the login flow when false and redirect the user home. Even if they
 * forget, the stale token is gone — the worst case is an unauthenticated user,
 * not wrong-account impersonation.
 */
export function setAuthTokenWithReadback(
  token: string | null | undefined,
  context?: { flowId?: string | null; source?: string },
): boolean {
  const flowId = context?.flowId ?? null;

  if (typeof token !== 'string' || token.length === 0) {
    captureAuthError('auth.token_missing_at_write', {
      flowId,
      source: context?.source,
      tokenType: typeof token,
    });
    return false;
  }

  safeLocalStorage.setItem(AUTH_TOKEN_KEY, token);
  const readback = safeLocalStorage.getItem(AUTH_TOKEN_KEY);
  if (readback === token) return true;
  const readbackLen = readback?.length ?? 0;
  const readbackIsNull = readback === null;
  // Clear BEFORE capturing so Sentry has an accurate post-state.
  safeLocalStorage.removeItem(AUTH_TOKEN_KEY);
  captureAuthError('auth.token_write_readback_mismatch', {
    flowId,
    tokenLen: token.length,
    readbackLen,
    readbackIsNull,
  });
  return false;
}

/**
 * Clear auth token
 */
export function clearAuthToken(): void {
  safeLocalStorage.removeItem(AUTH_TOKEN_KEY);
}

export function generateFlowId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Get the current diagnostic flow id if it is still fresh.
 */
export function getFlowId(): string | null {
  try {
    const flowId = sessionStorage.getItem(FLOW_ID_KEY);
    const createdAtRaw = sessionStorage.getItem(FLOW_ID_CREATED_AT_KEY);

    if (!flowId || !createdAtRaw) {
      clearFlowId();
      return null;
    }

    const createdAt = Number(createdAtRaw);
    if (!Number.isFinite(createdAt) || Date.now() - createdAt > FLOW_ID_TTL_MS) {
      clearFlowId();
      return null;
    }

    return flowId;
  } catch {
    return null;
  }
}

/**
 * Persist the current diagnostic flow id for a short period so follow-up
 * breadcrumbs can be correlated inside the restaurant domain.
 */
export function setFlowId(flowId: string): void {
  try {
    sessionStorage.setItem(FLOW_ID_KEY, flowId);
    sessionStorage.setItem(FLOW_ID_CREATED_AT_KEY, String(Date.now()));
  } catch {
    // Ignore storage failures. Diagnostics should never break auth flow.
  }
}

/**
 * Clear diagnostic flow id.
 */
export function clearFlowId(): void {
  try {
    sessionStorage.removeItem(FLOW_ID_KEY);
    sessionStorage.removeItem(FLOW_ID_CREATED_AT_KEY);
  } catch {
    // Ignore storage failures. Diagnostics should never break auth flow.
  }
}

/**
 * Map HTTP status to error code
 */
function getErrorCode(status?: number): ApiErrorCode {
  if (!status) return 'NETWORK_ERROR';

  switch (status) {
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 422:
      return 'VALIDATION_ERROR';
    default:
      if (status >= 500) return 'SERVER_ERROR';
      return 'UNKNOWN_ERROR';
  }
}

/**
 * Create configured Axios instance
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: getApiBaseUrl(),
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  // Auth-sensitive endpoints that warrant diagnostic breadcrumbs
  const AUTH_DIAGNOSTIC_URLS = [
    '/api/get-user-info',
    '/api/update-user-info',
    '/traditional-grab',
    '/deposit-grab',
  ];

  // Request interceptor - add auth token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Check if we should skip auth
      const skipAuth = (config as InternalAxiosRequestConfig & { skipAuth?: boolean }).skipAuth;

      if (!skipAuth) {
        const token = getAuthToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Diagnostic breadcrumb for auth-sensitive requests.
        // Captures JWT structural metadata (not content) to detect truncation
        // or corruption before the request leaves the browser.
        const url = config.url || '';
        if (AUTH_DIAGNOSTIC_URLS.some((u) => url.includes(u))) {
          Sentry.addBreadcrumb({
            category: 'auth-request',
            message: url,
            level: 'info',
            data: buildAuthRequestBreadcrumbData({
              url,
              token: token ?? null,
              authHeaderAttached: !!config.headers?.Authorization,
              skipAuth: !!skipAuth,
              flowId: getFlowId(),
            }),
          });
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - transform errors
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiErrorResponse>) => {
      const endpoint = error.config?.url;
      const method = error.config?.method?.toUpperCase();

      // Handle timeout
      if (error.code === 'ECONNABORTED') {
        throw new ApiError('Request timeout', undefined, 'TIMEOUT_ERROR', undefined, undefined, endpoint, method);
      }

      // Handle network errors
      if (!error.response) {
        throw new ApiError(
          'Network error - please check your connection',
          undefined,
          'NETWORK_ERROR',
          undefined,
          undefined,
          endpoint,
          method,
        );
      }

      const { status, data } = error.response;
      const message = data?.errorMessage || data?.error || data?.message || error.message || 'An error occurred';
      const code = getErrorCode(status);
      const errors = data?.errors;
      // Prefer the explicit `code` field; fall back to `status` for endpoints
      // (e.g. /api/reservation-action) that return their machine-readable
      // failure code under `status` instead of `code`.
      const serverCode =
        typeof data?.code === 'string'
          ? data.code
          : typeof data?.status === 'string'
            ? data.status
            : undefined;

      if (status === 401) {
        const url = error.config?.url || '';
        const hadToken = !!getAuthToken();
        const hadAuthHeader = !!error.config?.headers?.Authorization;
        const optionalAuth = !!(error.config as InternalAxiosRequestConfig & { optionalAuth?: boolean }).optionalAuth;
        const flowId = getFlowId();

        const breadcrumbData = buildAuth401BreadcrumbData({
          flowId,
          hadToken,
          hadAuthHeader,
          optionalAuth,
          error,
        });

        Sentry.addBreadcrumb({
          category: 'auth-response-401',
          message: url,
          level: 'warning',
          data: breadcrumbData,
        });

        clearAuthToken();

        const wasActiveSession = hadToken && hadAuthHeader && !optionalAuth;
        if (wasActiveSession) {
          // Dedup: multiple 401s from the same flow+endpoint in a 60s window
          // collapse to one Sentry event and one `auth:expired` dispatch.
          // Prevents logout-cascade when React Query, websocket, and
          // background polls all hit the same stale token at once.
          if (shouldCapture401(flowId, endpoint)) {
            captureAuthError(
              'auth.unexpected_401',
              {
                flowId,
                endpoint,
                method,
                url,
                status: 401,
                serverMessage: breadcrumbData.serverMessage,
                debugId: breadcrumbData.debugId,
                responseBytes: breadcrumbData.responseBytes,
              },
              { level: 'warning' },
            );
            window.dispatchEvent(
              new CustomEvent('auth:expired', {
                detail: { url, flowId, from: window.location.pathname },
              }),
            );
          }
        }
      }

      throw new ApiError(message, status, code, errors, serverCode, endpoint, method);
    }
  );

  return client;
}

/**
 * Main API client instance
 */
export const apiClient = createApiClient();

/**
 * Generic request function with retry support
 */
async function request<T>(
  config: AxiosRequestConfig & RequestConfig
): Promise<T> {
  const { retries = 0, skipAuth, ...axiosConfig } = config;

  // Add custom properties to config
  const requestConfig = {
    ...axiosConfig,
    skipAuth,
  } as InternalAxiosRequestConfig & { skipAuth?: boolean };

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await apiClient.request<T>(requestConfig);
      return response.data;
    } catch (error) {
      lastError = error as Error;

      // Don't retry on auth or validation errors
      if (error instanceof ApiError) {
        if (error.isAuthError() || error.isValidationError()) {
          throw error;
        }
      }

      // If we have retries left, wait before retrying
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }
  }

  throw lastError;
}

/**
 * API methods with TypeScript generics
 */
export const api = {
  /**
   * GET request
   */
  get: <T>(url: string, config?: RequestConfig & AxiosRequestConfig) =>
    request<T>({ ...config, method: 'GET', url }),

  /**
   * POST request
   */
  post: <T>(url: string, data?: unknown, config?: RequestConfig & AxiosRequestConfig) =>
    request<T>({ ...config, method: 'POST', url, data }),

  /**
   * PUT request
   */
  put: <T>(url: string, data?: unknown, config?: RequestConfig & AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PUT', url, data }),

  /**
   * PATCH request
   */
  patch: <T>(url: string, data?: unknown, config?: RequestConfig & AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PATCH', url, data }),

  /**
   * DELETE request
   */
  delete: <T>(url: string, config?: RequestConfig & AxiosRequestConfig) =>
    request<T>({ ...config, method: 'DELETE', url }),
};

export default apiClient;
